'use client';

import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';

import { TextVariants } from '@lichtblick/theme';

import { SidebarSideEnum } from './PanelLayout.enums';
import { PanelLoaderOverlay } from './PanelLayout.styles';

import { PacmanPanelLoader, SkeletonComponent } from '../Skeleton';
import { Text } from '../Text';

import type { PanelTab } from './PanelTabHeader';
import type { ReactNode } from 'react';

type PanelLayoutContextValue = {
  hasLeftSidebar: boolean;
  hasRightSidebar: boolean;
  headerContent: ReactNode;
  headerTabs: PanelTab[] | null;
  headerTitleText: string | null;
  hideLoader: () => void;
  isLeftCollapsed: boolean;
  isLeftSidebarPadded: boolean;
  isLoaderVisible: boolean;
  isMainContentPadded: boolean;
  isRightCollapsed: boolean;
  isRightSidebarPadded: boolean;
  setHeaderContent: (content: ReactNode) => void;
  setHeaderTabs: (tabs: PanelTab[] | null) => void;
  setHeaderTitleText: (title: string | null) => void;
  setLoaderVisible: (visible: boolean) => void;
  setMainContentPadded: (padded: boolean) => void;
  setSidebarCollapsedState: (state: { leftCollapsed: boolean; rightCollapsed: boolean }) => void;
  setSidebarContent: (side: SidebarSideEnum, options: { active: boolean; padded?: boolean }) => void;
  setSidebarPortalTargetLeft: (el: Element | null) => void;
  setSidebarPortalTargetRight: (el: Element | null) => void;
  showLoader: () => void;
  sidebarPortalTargetLeft: Element | null;
  sidebarPortalTargetRight: Element | null;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
};

const noop = () => undefined;

const defaultValue: PanelLayoutContextValue = {
  hasLeftSidebar: false,
  hasRightSidebar: false,
  headerContent: null,
  headerTabs: null,
  headerTitleText: null,
  hideLoader: noop,
  isLeftCollapsed: false,
  isLeftSidebarPadded: false,
  isLoaderVisible: false,
  isMainContentPadded: false,
  isRightCollapsed: false,
  isRightSidebarPadded: false,
  setHeaderContent: noop,
  setHeaderTabs: noop,
  setHeaderTitleText: noop,
  setMainContentPadded: noop,
  setSidebarContent: noop,
  setSidebarCollapsedState: noop,
  setSidebarPortalTargetLeft: noop,
  setSidebarPortalTargetRight: noop,
  setLoaderVisible: noop,
  showLoader: noop,
  sidebarPortalTargetLeft: null,
  sidebarPortalTargetRight: null,
  toggleLeftSidebar: noop,
  toggleRightSidebar: noop,
};

const PanelLayoutContext = createContext<PanelLayoutContextValue>(defaultValue);

export const PanelLayoutProvider = ({ children }: { children: ReactNode }) => {
  const [hasLeftSidebar, setHasLeftSidebar] = useState(false);
  const [hasRightSidebar, setHasRightSidebar] = useState(false);
  const [isLoaderVisible, setLoaderVisible] = useState(false);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isLeftSidebarPadded, setIsLeftSidebarPadded] = useState(false);
  const [isMainContentPadded, setIsMainContentPadded] = useState(false);
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const [isRightSidebarPadded, setIsRightSidebarPadded] = useState(false);
  const [headerContent, setHeaderContent] = useState<ReactNode>(null);
  const [headerTabs, setHeaderTabs] = useState<PanelTab[] | null>(null);
  const [headerTitleText, setHeaderTitleText] = useState<string | null>(null);
  const [sidebarPortalTargetLeft, setSidebarPortalTargetLeft] = useState<Element | null>(null);
  const [sidebarPortalTargetRight, setSidebarPortalTargetRight] = useState<Element | null>(null);

  const setSidebarContent = useCallback((side: SidebarSideEnum, options: { active: boolean; padded?: boolean }) => {
    const { active, padded = false } = options;

    if (side === SidebarSideEnum.Left) {
      setHasLeftSidebar(active);
      setIsLeftSidebarPadded(active ? padded : false);
    } else {
      setHasRightSidebar(active);
      setIsRightSidebarPadded(active ? padded : false);
    }
  }, []);

  const setSidebarCollapsedState = useCallback((state: { leftCollapsed: boolean; rightCollapsed: boolean }) => {
    setIsLeftCollapsed(state.leftCollapsed);
    setIsRightCollapsed(state.rightCollapsed);
  }, []);

  const toggleLeftSidebar = useCallback(() => {
    setIsLeftCollapsed((prev) => !prev);
  }, []);

  const toggleRightSidebar = useCallback(() => {
    setIsRightCollapsed((prev) => !prev);
  }, []);

  const value = useMemo<PanelLayoutContextValue>(
    () => ({
      hasLeftSidebar,
      hasRightSidebar,
      headerContent,
      headerTabs,
      headerTitleText,
      hideLoader: () => setLoaderVisible(false),
      isLeftCollapsed,
      isLeftSidebarPadded,
      isLoaderVisible,
      isMainContentPadded,
      isRightCollapsed,
      isRightSidebarPadded,
      setHeaderContent,
      setHeaderTabs,
      setHeaderTitleText,
      setMainContentPadded: setIsMainContentPadded,
      setSidebarContent,
      setSidebarCollapsedState,
      setSidebarPortalTargetLeft,
      setSidebarPortalTargetRight,
      setLoaderVisible,
      showLoader: () => setLoaderVisible(true),
      sidebarPortalTargetLeft,
      sidebarPortalTargetRight,
      toggleLeftSidebar,
      toggleRightSidebar,
    }),
    [
      hasLeftSidebar,
      hasRightSidebar,
      headerContent,
      headerTabs,
      headerTitleText,
      isLeftCollapsed,
      isLeftSidebarPadded,
      isLoaderVisible,
      isMainContentPadded,
      isRightCollapsed,
      isRightSidebarPadded,
      setSidebarCollapsedState,
      setSidebarContent,
      setSidebarPortalTargetLeft,
      setSidebarPortalTargetRight,
      setHeaderTitleText,
      sidebarPortalTargetLeft,
      sidebarPortalTargetRight,
      toggleLeftSidebar,
      toggleRightSidebar,
    ],
  );

  return <PanelLayoutContext.Provider value={value}>{children}</PanelLayoutContext.Provider>;
};

export const usePanelLayout = () => useContext(PanelLayoutContext);

export const PanelLayoutLoader = () => {
  const { isLoaderVisible } = usePanelLayout();

  if (!isLoaderVisible) {
    return null;
  }

  return (
    <PanelLoaderOverlay>
      <PacmanPanelLoader />
    </PanelLoaderOverlay>
  );
};

/**
 * Sets the panel sidebar content for the current route.
 * Content is cleared when the component unmounts.
 */
const SidebarContent = ({
  children,
  padded = false,
  side = SidebarSideEnum.Left,
}: {
  children: ReactNode;
  padded?: boolean;
  side?: SidebarSideEnum;
}) => {
  const { setSidebarContent, sidebarPortalTargetLeft, sidebarPortalTargetRight } = usePanelLayout();

  // Signal to the layout that a sidebar is active (drives hasLeftPanel / hasRightPanel availability checks).
  // We store a boolean sentinel rather than the children, since children now render via a portal.
  useEffect(() => {
    setSidebarContent(side, { active: true, padded });

    return () => setSidebarContent(side, { active: false });
  }, [padded, setSidebarContent, side]);

  const target = side === SidebarSideEnum.Left ? sidebarPortalTargetLeft : sidebarPortalTargetRight;

  if (!target) {
    return null;
  }

  return createPortal(children, target);
};

export const LeftSidebar = ({ children, padded }: { children: ReactNode; padded?: boolean }) => (
  <SidebarContent padded={padded} side={SidebarSideEnum.Left}>
    {children}
  </SidebarContent>
);

export const RightSidebar = ({ children, padded }: { children: ReactNode; padded?: boolean }) => (
  <SidebarContent padded={padded} side={SidebarSideEnum.Right}>
    {children}
  </SidebarContent>
);

export const PanelMainContent = ({ children, padded = false }: { children?: ReactNode; padded?: boolean }) => {
  const { setMainContentPadded } = usePanelLayout();

  useEffect(() => {
    setMainContentPadded(padded);

    return () => setMainContentPadded(false);
  }, [padded, setMainContentPadded]);

  return <>{children}</>;
};

/**
 * Sets extra header content for the current route.
 * Content is cleared when the component unmounts.
 */
export const PanelHeaderContent = ({ children }: { children: ReactNode }) => {
  const { setHeaderContent } = usePanelLayout();

  useEffect(() => {
    setHeaderContent(children);

    return () => setHeaderContent(null);
  }, [children, setHeaderContent]);

  return null;
};

/**
 * Sets header tabs for the current route.
 * Tabs are cleared when the component unmounts.
 */
export const PanelHeaderTabs = ({ tabs }: { tabs: PanelTab[] }) => {
  const { setHeaderTabs } = usePanelLayout();

  useEffect(() => {
    setHeaderTabs(tabs);

    return () => setHeaderTabs(null);
  }, [tabs, setHeaderTabs]);

  return null;
};

type PanelLayoutTitleProps = {
  children?: ReactNode;
  isLoading?: boolean;
  loading?: boolean;
};

const extractTextOnlyTitle = (value: ReactNode): string | null => {
  const normalizedChildren = Children.toArray(value);

  if (normalizedChildren.length === 0) {
    return null;
  }

  if (!normalizedChildren.every((child) => typeof child === 'string' || typeof child === 'number')) {
    return null;
  }

  const text = normalizedChildren.join('');

  return text.length > 0 ? text : null;
};

export const PanelLayoutTitle = ({ children, isLoading, loading = false }: PanelLayoutTitleProps) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const normalizedChildren = useMemo(() => Children.toArray(children), [children]);
  const areTextOnly =
    normalizedChildren.length > 0 &&
    normalizedChildren.every((child) => typeof child === 'string' || typeof child === 'number');
  const shouldShowLoading = isLoading ?? loading;

  if (shouldShowLoading) {
    return <SkeletonComponent height="1rem" width="10rem" />;
  }

  if (areTextOnly) {
    return <Text $variant={isMobile ? TextVariants.title.m : TextVariants.header.m}>{children}</Text>;
  }

  return <>{children}</>;
};

export const PanelHeader = ({
  children,
  isLoading,
  tabs,
  title,
}: {
  children?: ReactNode;
  isLoading?: boolean;
  tabs?: PanelTab[];
  title?: ReactNode;
}) => {
  const { setHeaderContent, setHeaderTabs, setHeaderTitleText } = usePanelLayout();
  const normalizedChildren = useMemo(() => Children.toArray(children), [children]);

  const parsedChildren = useMemo(() => {
    const contentNodes: ReactNode[] = [];
    let tabsFromChildren: PanelTab[] | undefined;
    let titleFromChildren: ReactNode | undefined;

    for (const child of normalizedChildren) {
      if (!isValidElement(child)) {
        contentNodes.push(child);
        continue;
      }

      if (child.type === PanelHeaderTabs && isValidElement<{ tabs: PanelTab[] }>(child)) {
        tabsFromChildren = child.props.tabs;
        continue;
      }

      if (child.type === PanelLayoutTitle) {
        titleFromChildren = child;
        continue;
      }

      contentNodes.push(child);
    }

    return { contentNodes, tabsFromChildren, titleFromChildren };
  }, [normalizedChildren]);

  if (title != null && parsedChildren.titleFromChildren != null) {
    throw new Error('PanelComponents.Header received both `title` prop and `Header.Title` child.');
  }

  if (tabs != null && parsedChildren.tabsFromChildren != null) {
    throw new Error('PanelComponents.Header received both `tabs` prop and `Header.Tabs` child.');
  }

  const resolvedTabs = tabs ?? parsedChildren.tabsFromChildren ?? null;
  const titleFromChildrenProps = useMemo(
    () =>
      parsedChildren.titleFromChildren != null &&
      isValidElement<PanelLayoutTitleProps>(parsedChildren.titleFromChildren)
        ? parsedChildren.titleFromChildren.props
        : null,
    [parsedChildren.titleFromChildren],
  );
  const resolvedTitleLoading = useMemo(
    () =>
      title != null
        ? (isLoading ?? false)
        : (isLoading ?? titleFromChildrenProps?.isLoading ?? titleFromChildrenProps?.loading ?? false),
    [isLoading, title, titleFromChildrenProps?.isLoading, titleFromChildrenProps?.loading],
  );
  const resolvedTitleNode = useMemo(
    () =>
      title != null ? (
        <PanelLayoutTitle isLoading={isLoading}>{title}</PanelLayoutTitle>
      ) : parsedChildren.titleFromChildren != null &&
        isValidElement<PanelLayoutTitleProps>(parsedChildren.titleFromChildren) ? (
        cloneElement(parsedChildren.titleFromChildren, {
          isLoading: isLoading ?? parsedChildren.titleFromChildren.props.isLoading,
        })
      ) : (
        parsedChildren.titleFromChildren
      ),
    [isLoading, parsedChildren.titleFromChildren, title],
  );
  const resolvedTitleText = useMemo(
    () =>
      resolvedTitleLoading
        ? null
        : extractTextOnlyTitle(title != null ? title : (titleFromChildrenProps?.children ?? null)),
    [resolvedTitleLoading, title, titleFromChildrenProps?.children],
  );
  const resolvedContent = useMemo(
    () =>
      resolvedTitleNode ? (
        <>
          {resolvedTitleNode}
          {parsedChildren.contentNodes}
        </>
      ) : parsedChildren.contentNodes.length > 0 ? (
        parsedChildren.contentNodes
      ) : null,
    [parsedChildren.contentNodes, resolvedTitleNode],
  );

  useEffect(() => {
    setHeaderContent(resolvedContent);

    return () => setHeaderContent(null);
  }, [resolvedContent, setHeaderContent]);

  useEffect(() => {
    setHeaderTabs(resolvedTabs);

    return () => setHeaderTabs(null);
  }, [resolvedTabs, setHeaderTabs]);

  useEffect(() => {
    setHeaderTitleText(resolvedTitleText);

    return () => setHeaderTitleText(null);
  }, [resolvedTitleText, setHeaderTitleText]);

  return null;
};
